import { useContext } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, Tab, Tabs, Tooltip } from '@mui/material';

import { LocaleContext } from '@/Contexts/LocaleContext';

import { getModalTabConfig, ModalTab } from '../models/modalTabs';

type Props = {
  activeTab: ModalTab;
  onChange: (tab: ModalTab) => void;
  dirtyTabs: Record<ModalTab, boolean>;
  savedTabs: Record<ModalTab, boolean>;
  errorTabs: Record<ModalTab, boolean>;
  validationErrorTabs?: Partial<Record<ModalTab, boolean>>;
  savingTab: ModalTab | null;
};

export function PartnerModalTabs({
  activeTab,
  onChange,
  dirtyTabs,
  savedTabs,
  errorTabs,
  validationErrorTabs,
  savingTab,
}: Props) {
  const { translatedText } = useContext(LocaleContext);
  const modalTabConfig = getModalTabConfig(translatedText);

  const renderTabLabel = (tab: ModalTab, text: string) => {
    const isSavingTab = savingTab === tab;
    const hasError = errorTabs[tab] || Boolean(validationErrorTabs?.[tab]);
    const hasDraft = dirtyTabs[tab];
    const hasSaved = !hasDraft && savedTabs[tab] && !hasError;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontWeight: 900,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          color: 'taiga.main',
        }}
      >
        <span>{text}</span>

        {isSavingTab ? (
          <Tooltip title={translatedText.partnerUpdateModal.tooltipSaving}>
            <CircularProgress size={16} />
          </Tooltip>
        ) : hasError ? (
          <Tooltip title={translatedText.partnerUpdateModal.tooltipError}>
            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />
          </Tooltip>
        ) : hasDraft ? (
          <Tooltip title={translatedText.partnerUpdateModal.tooltipHasUnsavedChanges}>
            <EditIcon sx={{ color: 'warning.main', fontSize: 18 }} />
          </Tooltip>
        ) : hasSaved ? (
          <Tooltip title={translatedText.partnerUpdateModal.tooltipSaved}>
            <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  return (
    <Tabs value={activeTab} onChange={(_e, value: ModalTab) => onChange(value)} variant='fullWidth'>
      {modalTabConfig.map((tab) => (
        <Tab key={tab.id} value={tab.id} label={renderTabLabel(tab.id, tab.label)} />
      ))}
    </Tabs>
  );
}
